import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { ListItemText, ListSubheader, MenuItem } from "@mui/material"
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid"

import Table from "@/components/table/Table"
import Pagination from "@/components/table/Pagination"
import Checkbox, { BaseCheckbox } from "@/components/form/checkbox/Checkbox"
import Selector from "@/components/form/selector/Selector"
import { usePlayerGroupsQuery } from "@/hooks/api/useSettingQuery"
import { usePositionsQuery } from "@/hooks/api/usePositionQuery"
import { CheckboxColor } from "@/types/components/checkbox"
import type { PlayerSetting } from "@/types/setting"

/**
 * Filter Preset 에디터에 인라인으로 노출되는 선수 선택 섹션.
 * Group/Position 필터 + 체크박스 플레이어 테이블. 선택 변경 시 즉시 커밋.
 */

const POSITION_COL_WIDTH = 100

interface Props {
  players: PlayerSetting[]
  selectedPlayerIds: number[]
  onSelectionChange: (ids: number[]) => void
}

export default function PlayerListSelector({
  players,
  selectedPlayerIds,
  onSelectionChange,
}: Props) {
  const { t } = useTranslation()

  const [selectedGroupList, setSelectedGroupList] = useState<number[]>([])
  const [selectedPositionList, setSelectedPositionList] = useState<string[]>([])

  const isFirstRender = useRef(true)

  // --- Queries ---
  const { data: playerGroupList } = usePlayerGroupsQuery()
  // 드롭다운은 그룹 클러스터 순서(ordering=group)로 받아 그대로 신뢰.
  const { data: positionList } = usePositionsQuery("group")

  // --- Derived options ---
  const playerGroupOptions = useMemo(() => {
    if (!playerGroupList) return []
    return playerGroupList.teamPlayerGroups
      .filter((g) => players.some((p) => p.idfPlayerGroup === g.idfTeamPlayerGroup))
      .map((g) => ({ value: g.idfTeamPlayerGroup as number, label: g.name }))
  }, [playerGroupList, players])

  const allGroupValues = useMemo(() => playerGroupOptions.map((o) => o.value), [playerGroupOptions])

  const positionOptions = useMemo(() => {
    if (!positionList) return []
    const playerPositions = new Set(players.map((p) => p.position))
    return positionList
      .filter((p) => playerPositions.has(p.code))
      .map((p) => ({ value: p.code as string, label: p.code as string, group: p.group as string }))
  }, [positionList, players])

  const allPositionValues = useMemo(() => positionOptions.map((o) => o.value), [positionOptions])

  const positionGroupOptions = useMemo(() => {
    const grouped = positionOptions.reduce(
      (acc, opt) => {
        if (!acc[opt.group]) acc[opt.group] = []
        acc[opt.group].push(opt)
        return acc
      },
      {} as Record<string, typeof positionOptions>,
    )
    return Object.entries(grouped).map(([group, options]) => ({ group, options }))
  }, [positionOptions])

  // --- Initial selection (전체) ---
  useEffect(() => {
    if (allGroupValues.length) setSelectedGroupList(allGroupValues)
  }, [allGroupValues])

  useEffect(() => {
    if (allPositionValues.length) setSelectedPositionList(allPositionValues)
  }, [allPositionValues])

  // --- Filtered players ---
  const filteredPlayers = useMemo(() => {
    return players
      .filter((player) => {
        const groupOk =
          selectedGroupList.length === allGroupValues.length
            ? true
            : selectedGroupList.includes(player.idfPlayerGroup ?? -1)
        const posOk = selectedPositionList.includes(player.position)
        return groupOk && posOk
      })
      .sort((a, b) => a.uniformNumber - b.uniformNumber)
  }, [players, selectedGroupList, selectedPositionList, allGroupValues])

  // --- Selection model (필터에 보이는 것만 DataGrid에 전달) ---
  const rowSelectionModel: GridRowSelectionModel = useMemo(() => {
    const visible = new Set(filteredPlayers.map((p) => p.idfPlayer))
    const ids = selectedPlayerIds.filter((id) => visible.has(id))
    return { type: "include", ids: new Set(ids) }
  }, [selectedPlayerIds, filteredPlayers])

  // --- 유효하지 않은 선택 정리 (팀 전환 등) ---
  useEffect(() => {
    const validIds = new Set(players.map((p) => p.idfPlayer))
    const filtered = selectedPlayerIds.filter((id) => validIds.has(id))
    if (filtered.length !== selectedPlayerIds.length) {
      onSelectionChange(filtered)
    }
  }, [players])

  // --- Handlers ---
  const commit = (ids: number[]) => onSelectionChange(ids)

  const onClickRow = (item: { row: PlayerSetting }) => {
    const id = item.row.idfPlayer
    commit(
      selectedPlayerIds.includes(id)
        ? selectedPlayerIds.filter((x) => x !== id)
        : [...selectedPlayerIds, id],
    )
  }

  // Group filter
  const toggleGroup = (value: number) => {
    setSelectedGroupList((prev) =>
      prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value],
    )
  }
  const toggleAllGroups = () => {
    setSelectedGroupList(selectedGroupList.length === allGroupValues.length ? [] : allGroupValues)
  }

  // Position filter
  const togglePosition = (value: string) => {
    setSelectedPositionList((prev) =>
      prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value],
    )
  }
  const toggleAllPositions = () => {
    setSelectedPositionList(
      selectedPositionList.length === allPositionValues.length ? [] : allPositionValues,
    )
  }

  const renderMultiValue = (count: number, total: number) => {
    if (count === total) return t("all")
    return `${count} ${t("selected", "selected")}`
  }

  // --- Table columns ---
  const tableColumns: GridColDef[] = [
    {
      field: "name",
      renderHeader: () => <p>{t("playerName", "Player Name")}</p>,
      renderCell: (params) => (
        <p>
          {params.row.uniformNumber} {params.row.name}
        </p>
      ),
      flex: 1,
      minWidth: 200,
      sortable: false,
    },
    {
      field: "position",
      renderHeader: () => <p>{t("position")}</p>,
      width: POSITION_COL_WIDTH,
      sortable: false,
    },
  ]

  return (
    <div className="PlayerListSelector">
      <div className="FilterPresetEditor__grid FilterPresetEditor__grid--2col">
        {/* Player Group */}
        <div className="FilterPresetEditor__field">
          <label className="FilterPresetEditor__label">{t("playerGroup", "Player Group")}</label>
          <Selector
            type="select"
            multiple
            displayEmpty
            value={selectedGroupList.map(String)}
            onChange={(_e: { target: { value: any } }) => {}}
            renderValue={() => renderMultiValue(selectedGroupList.length, allGroupValues.length)}>
            <MenuItem
              key="all-group"
              onClick={toggleAllGroups}
              sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
              <Checkbox
                checked={selectedGroupList.length === allGroupValues.length}
                indeterminate={
                  selectedGroupList.length > 0 && selectedGroupList.length < allGroupValues.length
                }
                size="medium"
                color={CheckboxColor.PLATINUM}
              />
              <ListItemText primary={t("all")} />
            </MenuItem>
            {playerGroupOptions.map((opt) => (
              <MenuItem
                key={opt.value}
                value={String(opt.value)}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  toggleGroup(opt.value)
                }}>
                <Checkbox checked={selectedGroupList.includes(opt.value)} size="medium" />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Selector>
        </div>

        {/* Position */}
        <div className="FilterPresetEditor__field">
          <label className="FilterPresetEditor__label">{t("position")}</label>
          <Selector
            type="select"
            multiple
            displayEmpty
            value={selectedPositionList}
            onChange={(_e: { target: { value: any } }) => {}}
            renderValue={() =>
              renderMultiValue(selectedPositionList.length, allPositionValues.length)
            }>
            <MenuItem
              key="all-position"
              onClick={toggleAllPositions}
              sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
              <Checkbox
                checked={selectedPositionList.length === allPositionValues.length}
                indeterminate={
                  selectedPositionList.length > 0 &&
                  selectedPositionList.length < allPositionValues.length
                }
                size="medium"
                color={CheckboxColor.PLATINUM}
              />
              <ListItemText primary={t("all")} />
            </MenuItem>
            {positionGroupOptions.map((pg) => [
              <ListSubheader key={`pg-${pg.group}`}>{pg.group}</ListSubheader>,
              ...pg.options.map((opt) => (
                <MenuItem
                  key={opt.value}
                  value={opt.value}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    togglePosition(opt.value)
                  }}>
                  <Checkbox checked={selectedPositionList.includes(opt.value)} size="medium" />
                  <ListItemText primary={opt.label} />
                </MenuItem>
              )),
            ])}
          </Selector>
        </div>
      </div>

      {/* Player Table — fixed 420px with scroll */}
      <div className="PlayerListSelector__tableWrap">
        <Table
          className="PlayerListSelector__table"
          columns={tableColumns}
          rows={filteredPlayers}
          getRowId={(row) => row.idfPlayer}
          rowSelectionModel={rowSelectionModel}
          onRowClick={onClickRow}
          slots={{ pagination: Pagination }}
          selectionType="checkbox"
          columnHeaderHeight={41}
          rowHeight={44}
          disableColumnResize
          disableColumnMenu
          disableColumnSorting
          selectionElement={(props) => {
            const isHeader =
              (props as any).slotProps?.htmlInput?.["aria-label"]?.includes("all") ||
              (props as any)["aria-label"]?.includes("all")

            const onChangeCheckbox = (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
              if (isHeader) {
                const filteredIds = filteredPlayers.map((p) => p.idfPlayer)
                const allSelected = filteredIds.every((id) => selectedPlayerIds.includes(id))
                if (allSelected && filteredIds.length > 0) {
                  commit(selectedPlayerIds.filter((id) => !filteredIds.includes(id)))
                } else {
                  commit(Array.from(new Set([...selectedPlayerIds, ...filteredIds])))
                }
                isFirstRender.current = false
                return
              }
              isFirstRender.current = false
              return props.onChange?.(e, checked)
            }

            return (
              <BaseCheckbox
                checked={props.checked}
                indeterminate={props.indeterminate}
                onChange={onChangeCheckbox}
                disabled={props.disabled}
              />
            )
          }}
          onRowSelectionModelChange={(model) => {
            if (isFirstRender.current) {
              isFirstRender.current = false
              return
            }
            const filteredIds = filteredPlayers.map((p) => p.idfPlayer)
            const kept = selectedPlayerIds.filter((id) => !filteredIds.includes(id))
            const nextInFilter = filteredIds.filter((id) => model.ids.has(id))
            commit([...kept, ...nextInFilter])
          }}
          sx={{ width: "100%", height: "100%" }}
        />
      </div>
    </div>
  )
}
